import { stripTokenFromPath, withAuthToken } from './crossAppUrls';

/** Hosting platforms where the full hostname is the app apex (not company.portal.tld). */
const PLATFORM_APEX_SUFFIXES = [
	'.onrender.com',
	'.vercel.app',
	'.netlify.app',
	'.railway.app',
	'.fly.dev',
	'.pages.dev',
	'.herokuapp.com',
];

/**
 * Infra / product hosts — never treat these as a company portal subdomain.
 * `admin` is the System Admin app; bouncing SA users off it caused login loops.
 */
const RESERVED_SUBDOMAINS = new Set([
	'www',
	'admin',
	'api',
	'app',
	'mail',
	'ftp',
	'cdn',
	'static',
	'assets',
	'status',
	'docs',
]);

export const isPlatformManagedHostname = (
	hostname = window?.location?.hostname || '',
) => {
	const host = String(hostname || '')
		.split(':')[0]
		.toLowerCase();
	if (!host) return false;
	return PLATFORM_APEX_SUFFIXES.some((suffix) => host.endsWith(suffix));
};

export const getPortalSubdomain = (
	hostname = window?.location?.hostname || '',
) => {
	if (!hostname) return null;

	const host = hostname.split(':')[0].toLowerCase();
	const parts = host.split('.');
	const isLocalhostSubdomain = host.endsWith('.localhost');

	if (host === 'localhost' || host.startsWith('127.')) {
		return null;
	}

	// Render/Vercel/etc. app URLs are apex hosts, e.g. opspilotcrm-frontend-system-admin.onrender.com
	if (isPlatformManagedHostname(host)) {
		return null;
	}

	if (!isLocalhostSubdomain && parts.length < 3) {
		return null;
	}

	const firstPart = parts[0];
	if (!firstPart || RESERVED_SUBDOMAINS.has(firstPart)) {
		return null;
	}

	return firstPart;
};

/** Client-facing portal routes (/client, /client/login, …) — not CRM /clients. */
export const isClientPortalPath = (pathname = '') =>
	pathname === '/client' || pathname.startsWith('/client/');

export const getBaseDomain = (hostname = window?.location?.hostname || '') => {
	if (!hostname) return '';

	const host = hostname.split(':')[0].toLowerCase();
	const parts = host.split('.');

	if (host.endsWith('.localhost')) {
		return 'localhost';
	}

	// Keep the full Render/Vercel hostname — never collapse to onrender.com / vercel.app
	if (isPlatformManagedHostname(host)) {
		return host;
	}

	if (parts.length <= 2) {
		return host;
	}

	return parts.slice(-2).join('.');
};

/** Auth page on the apex host (no portal subdomain). */
export const buildApexUrl = (
	pathname = '/auth',
	hostname = window?.location?.hostname || '',
	protocol = window?.location?.protocol || 'https:',
	port = window?.location?.port || '',
	token = null,
) => {
	const portSuffix = port ? `:${port}` : '';
	const host = hostname.split(':')[0].toLowerCase();
	const cleanPath = stripTokenFromPath(pathname);

	let url;
	if (host === 'localhost' || host.startsWith('127.') || host.endsWith('.localhost')) {
		url = `${protocol}//localhost${portSuffix}${cleanPath}`;
	} else {
		const baseDomain = getBaseDomain(hostname);
		url = `${protocol}//${baseDomain}${portSuffix}${cleanPath}`;
	}

	return token ? withAuthToken(url, token) : url;
};

export const buildPortalUrl = (
	portalUrlPrefix,
	pathname = '/auth',
	hostname = window?.location?.hostname || '',
	protocol = window?.location?.protocol || 'https:',
	token = null,
) => {
	if (!portalUrlPrefix) return null;

	const host = hostname.split(':')[0].toLowerCase();
	const port = window?.location?.port ? `:${window.location.port}` : '';
	const cleanPath = stripTokenFromPath(pathname);

	let url;
	if (host.includes('localhost')) {
		url = `${protocol}//${portalUrlPrefix}.localhost${port}${cleanPath}`;
	} else {
		const parts = host.split('.');
		const baseDomain = parts.length > 2 ? parts.slice(-2).join('.') : host;
		url = `${protocol}//${portalUrlPrefix}.${baseDomain}${port}${cleanPath}`;
	}

	return token ? withAuthToken(url, token) : url;
};

export const getAuthCookieDomain = (
	hostname = window?.location?.hostname || '',
) => {
	if (!hostname) return null;

	const host = hostname.split(':')[0].toLowerCase();

	if (host.endsWith('.localhost') || host === 'localhost') {
		return '.localhost';
	}

	// Never set cookie domain to .onrender.com / .vercel.app (shared across unrelated apps)
	if (isPlatformManagedHostname(host)) {
		return process.env.REACT_APP_COOKIE_DOMAIN || null;
	}

	const parts = host.split('.');
	if (parts.length >= 2) {
		return `.${parts.slice(-2).join('.')}`;
	}

	return process.env.REACT_APP_COOKIE_DOMAIN || null;
};

export const getCookie = (name) => {
	if (typeof document === 'undefined') return null;
	const cookieString = document.cookie || '';
	const cookieMatch = cookieString.match(
		new RegExp(
			`(?:^|; )${name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1')}=([^;]*)`,
		),
	);
	return cookieMatch ? decodeURIComponent(cookieMatch[1]) : null;
};

export const clearAuthLocalStorage = () => {
	if (typeof localStorage === 'undefined') return;
	localStorage.removeItem('token');
	localStorage.removeItem('authenticatedUserInfo');
};

/** Remove auth cookies from the current host and shared parent domains. */
export const clearAuthCookies = () => {
	if (typeof document === 'undefined') return;

	const expired = 'Thu, 01 Jan 1970 00:00:00 GMT';
	const cookieNames = ['authToken'];
	const hostname = window.location.hostname;
	const securePart =
		window.location.protocol === 'https:' ? '; Secure' : '';
	const domainVariants = new Set(['', hostname]);

	const cookieDomain =
		process.env.REACT_APP_COOKIE_DOMAIN || getAuthCookieDomain(hostname);
	if (cookieDomain) {
		domainVariants.add(cookieDomain);
	}

	// Bare localhost is a separate origin from *.localhost subdomains.
	if (hostname.endsWith('.localhost')) {
		domainVariants.add('localhost');
	}

	for (const name of cookieNames) {
		for (const domain of domainVariants) {
			const domainPart = domain ? `; Domain=${domain}` : '';
			document.cookie = `${name}=; Path=/; Expires=${expired}; Max-Age=0; SameSite=Lax${securePart}${domainPart}`;
		}
	}
};

export const clearAllClientAuth = () => {
	clearAuthLocalStorage();
	clearAuthCookies();
};

/** Ask the API to clear the shared auth cookie (works with credentials). */
export const requestServerLogout = () => {
	const apiUrl = process.env.REACT_APP_API_URL;
	if (!apiUrl || typeof fetch === 'undefined') {
		return Promise.resolve();
	}

	return fetch(`${apiUrl}/api/auth/logout`, {
		method: 'POST',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			'x-portal-host': window.location.host,
		},
	}).catch(() => {});
};

/**
 * Read persisted auth token on first render.
 * URL ?token= handoff is applied in AuthenticatedUserInfoContext (useEffect)
 * so React Strict Mode does not strip the param before the session is saved.
 */
export const resolveInitialAuthToken = () => {
	if (typeof window === 'undefined') return null;

	const params = new URLSearchParams(window.location.search);
	if (params.get('loggedOut') === '1') {
		clearAllClientAuth();
		return null;
	}

	const urlToken = params.get('token');
	if (urlToken && !isClientPortalPath(window.location.pathname)) {
		localStorage.setItem('token', urlToken);
		params.delete('token');
		const remaining = params.toString();
		window.history.replaceState(
			{},
			document.title,
			`${window.location.pathname}${remaining ? `?${remaining}` : ''}`,
		);
		return urlToken;
	}

	const cookieToken = getCookie('authToken');
	const storageToken = localStorage.getItem('token');

	if (cookieToken && cookieToken !== storageToken) {
		localStorage.setItem('token', cookieToken);
	}

	return storageToken || cookieToken;
};

/** Persist a ?token= handoff after cross-host redirect (localhost ↔ portal subdomain). */
export const consumeUrlAuthToken = () => {
	if (typeof window === 'undefined') return null;

	const params = new URLSearchParams(window.location.search);
	const urlToken = params.get('token');
	if (!urlToken || isClientPortalPath(window.location.pathname)) return null;

	localStorage.setItem('token', urlToken);

	params.delete('token');
	const remaining = params.toString();
	window.history.replaceState(
		{},
		document.title,
		`${window.location.pathname}${remaining ? `?${remaining}` : ''}`,
	);

	return urlToken;
};

/**
 * After logout redirect, clear auth on this origin and continue the logout chain.
 * Returns true when the query param was handled (page is navigating away).
 */
export const consumeLoggedOutQueryParam = () => {
	if (typeof window === 'undefined') return false;

	const params = new URLSearchParams(window.location.search);
	if (params.get('loggedOut') !== '1') return false;

	requestServerLogout();
	clearAllClientAuth();

	const portalPrefix = params.get('portal');
	params.delete('loggedOut');
	params.delete('portal');

	const remaining = params.toString();
	const nextPath = `${window.location.pathname}${
		remaining ? `?${remaining}` : ''
	}`;

	if (portalPrefix) {
		const portalAuthUrl = buildPortalUrl(
			portalPrefix,
			'/auth?loggedOut=1',
		);
		if (portalAuthUrl) {
			window.location.replace(portalAuthUrl);
			return true;
		}
	}

	window.location.replace(nextPath);
	return true;
};

/**
 * Where to send the user after logout.
 * Portal users bounce through apex first so localhost-origin storage is wiped too.
 */
export const resolveLogoutAuthUrl = ({
	portalUrlPrefix = null,
	userRole = null,
	hostname = window?.location?.hostname || '',
	protocol = window?.location?.protocol || 'https:',
	port = window?.location?.port || '',
} = {}) => {
	const authPath = '/auth?loggedOut=1';
	const currentSubdomain = getPortalSubdomain(hostname);
	const isSystemAdmin = userRole === 'System Admin';
	const portalQuery = portalUrlPrefix
		? `&portal=${encodeURIComponent(portalUrlPrefix)}`
		: '';

	if (isSystemAdmin) {
		if (currentSubdomain) {
			return buildApexUrl(authPath, hostname, protocol, port);
		}
		return `${protocol}//${window.location.host}${authPath}`;
	}

	if (currentSubdomain) {
		return buildApexUrl(`${authPath}${portalQuery}`, hostname, protocol, port);
	}

	if (portalUrlPrefix) {
		const portalAuthUrl = buildPortalUrl(
			portalUrlPrefix,
			authPath,
			hostname,
			protocol,
		);
		if (portalAuthUrl) return portalAuthUrl;
	}

	return `${protocol}//${window.location.host}${authPath}`;
};

/** Company users must use their portal subdomain for authenticated app routes. */
export const isCompanyUserOnWrongPortalHost = ({
	userRole = null,
	portalUrlPrefix = null,
	hostname = window?.location?.hostname || '',
	pathname = window?.location?.pathname || '',
	isAuthenticated = false,
} = {}) => {
	if (!userRole || userRole === 'System Admin') return false;
	if (!portalUrlPrefix) return false;

	if (getPortalSubdomain(hostname) === portalUrlPrefix) return false;

	// Guests may use marketing/auth pages on any host.
	if (!isAuthenticated) {
		const guestPaths = ['/', '/auth', '/signup'];
		if (guestPaths.includes(pathname) || pathname.startsWith('/reset-password')) {
			return false;
		}
	}

	return true;
};

/** System admins must use the apex host when authenticated. */
export const isSystemAdminOnPortalHost = ({
	userRole = null,
	hostname = window?.location?.hostname || '',
	isAuthenticated = false,
} = {}) => {
	if (!isAuthenticated || userRole !== 'System Admin') return false;
	return !!getPortalSubdomain(hostname);
};

/**
 * Cross-origin redirect after login when the user must be on a different host.
 * Returns null when the user can stay on the current origin.
 */
export const resolvePostAuthRedirectUrl = ({
	userRole = null,
	portalUrlPrefix = null,
	dashboardPath = '/',
	hostname = window?.location?.hostname || '',
	protocol = window?.location?.protocol || 'https:',
	port = window?.location?.port || '',
	token = null,
} = {}) => {
	const currentSubdomain = getPortalSubdomain(hostname);

	if (userRole === 'System Admin') {
		if (currentSubdomain) {
			return buildApexUrl(dashboardPath, hostname, protocol, port, token);
		}
		return null;
	}

	if (portalUrlPrefix && currentSubdomain !== portalUrlPrefix) {
		return buildPortalUrl(
			portalUrlPrefix,
			dashboardPath,
			hostname,
			protocol,
			token,
		);
	}

	return null;
};

/** Redirect authenticated users to the correct host, preserving session via token. */
export const buildAuthenticatedHostRedirectUrl = ({
	userRole = null,
	portalUrlPrefix = null,
	pathname = window?.location?.pathname || '/',
	search = window?.location?.search || '',
	token = null,
	hostname = window?.location?.hostname || '',
	protocol = window?.location?.protocol || 'https:',
	port = window?.location?.port || '',
	dashboardPath = '/',
} = {}) => {
	const guestPaths = ['/', '/auth', '/signup'];
	const targetPath = guestPaths.includes(pathname)
		? dashboardPath
		: stripTokenFromPath(`${pathname}${search}`);

	if (userRole === 'System Admin' && getPortalSubdomain(hostname)) {
		return buildApexUrl(targetPath, hostname, protocol, port, token);
	}

	if (
		portalUrlPrefix &&
		isCompanyUserOnWrongPortalHost({
			userRole,
			portalUrlPrefix,
			hostname,
			pathname,
			isAuthenticated: true,
		})
	) {
		return buildPortalUrl(
			portalUrlPrefix,
			targetPath,
			hostname,
			protocol,
			token,
		);
	}

	return null;
};

/** Paths guests may open on a company portal subdomain (everything else → /auth). */
export const PORTAL_GUEST_ALLOWED_PATHS = [
	'/auth',
	'/client/login',
	'/privacy',
	'/terms',
	'/cookies',
	'/privacy.html',
	'/terms.html',
	'/cookies.html',
];

export const isPortalGuestAllowedPath = (pathname = '') => {
	if (PORTAL_GUEST_ALLOWED_PATHS.includes(pathname)) return true;
	if (pathname.startsWith('/reset-password')) return true;
	if (pathname.startsWith('/client/invite')) return true;
	if (pathname.startsWith('/client/login')) return true;
	if (pathname.startsWith('/p/')) return true;
	if (pathname.startsWith('/f/')) return true;
	return false;
};

/** Logged-out visitors on a portal subdomain must use /auth only. */
export const shouldRedirectPortalGuestToAuth = ({
	hostname = window?.location?.hostname || '',
	pathname = window?.location?.pathname || '',
	isAuthenticated = false,
	isBootstrapping = false,
} = {}) => {
	if (isAuthenticated || isBootstrapping) return false;
	if (!getPortalSubdomain(hostname)) return false;
	return !isPortalGuestAllowedPath(pathname);
};
